import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { FeatureCollection, Point, MultiPolygon } from 'geojson';
import { Style, Stroke, Fill } from 'ol/style';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  CoreStoreProvider,
  LayerManagerConnect,
  LayerManagerMapButtonConnect,
  LegendConnect,
  OlMapViewConnect,
  TimeSliderConnect,
} from '@opengeoweb/core';
import {
  layerSelectReducersMap,
  layerSelectMiddlewares,
} from '@opengeoweb/layer-select';
import { PROJECTION, SessionStorageKey } from '@opengeoweb/shared';
import {
  configureOpenGeoWebStore,
  syncGroupsActions,
  defaultLayers,
  layerActions,
  mapActions,
  mapUtils,
  metronomeListener,
  syncGroupsListener,
} from '@opengeoweb/store';
import type { layerTypes, mapTypes } from '@opengeoweb/store';
import { ThemeProvider } from '@opengeoweb/theme';
import { LayerType } from '@opengeoweb/webmap';
import {
  OpenLayersFeatureLayer,
  MapControls,
  publicLayers,
} from '@opengeoweb/webmap-react';
import FlySafeConnect from './flysafe/components/FlySafeConnect';
import HideMapPin from './flysafe/components/HideMapPin';
import { getCachedStationLocations } from './flysafe/edr-api/cachedApi';
import { createRadarStyleFunction } from './flysafe/utils/radarStyles';
import { DAY_MS, WEEK_MS } from './flysafe/utils/timeUtils';
import countriesRaw from './static/countries.json';

const countries: FeatureCollection<MultiPolygon> =
  countriesRaw as FeatureCollection<MultiPolygon>;

export const initialBbox = {
  srs: 'EPSG:3857',
  bbox: {
    left: -450651.2255879827,
    bottom: 6490531.093143953,
    right: 1428345.8183648037,
    top: 7438773.776232235,
  },
};

interface UseDefaultMapSettingsProps {
  mapId?: string;
  layers?: layerTypes.Layer[];
  baseLayers?: layerTypes.Layer[];
  bbox?: mapTypes.Bbox;
  srs?: string;
  shouldAutoUpdate?: boolean;
}

export const useDefaultMapSettings = (
  props: UseDefaultMapSettingsProps = {},
): string | null => {
  const dispatch = useDispatch();
  const [defaultLayerId, setDefaultLayerId] = React.useState<string | null>(
    null,
  );

  useEffect(() => {
    const { mapId = 'test-map-1' } = props;
    const {
      layers = [],
      baseLayers = [
        { ...defaultLayers.baseLayerGrey, id: `layer-grey-${mapId}` },
        { ...defaultLayers.overLayer, id: `overlayer-${mapId}` },
      ],
      bbox = initialBbox.bbox,
      srs = initialBbox.srs,
      shouldAutoUpdate = true,
    } = props;
    // make sure all layers have a unique id before going forward
    const { layers: layersNewIds } =
      mapUtils.replaceLayerIdsToEnsureUniqueLayerIdsInStore({
        layers,
      });
    // set layers
    dispatch(
      layerActions.setLayers({
        mapId,
        layers: layersNewIds,
      }),
    );
    // set active layer if given otherwise to first layer
    const autoLayerId = layersNewIds[0]?.id;
    setDefaultLayerId(autoLayerId ?? null);
    dispatch(
      mapActions.setAutoLayerId({
        mapId,
        autoTimeStepLayerId: autoLayerId,
        autoUpdateLayerId: autoLayerId,
      }),
    );
    //  baseLayers
    dispatch(
      layerActions.setBaseLayers({
        mapId,
        layers: baseLayers,
      }),
    );
    // bbox
    dispatch(
      mapActions.setBbox({
        bbox,
        srs,
        mapId,
      }),
    );
    // auto update
    dispatch(mapActions.toggleAutoUpdate({ mapId, shouldAutoUpdate }));
    dispatch(
      mapActions.setAutoUpdateLayerId({ mapId, autoUpdateLayerId: undefined }),
    );
    // Disable auto time span so our manual span is not overridden
    dispatch(mapActions.toggleTimeSpanAuto({ mapId, timeSpanAuto: false }));
    // Time slider span: past week + 12h and next 12h

    const sliderSpanSec = (WEEK_MS + 2 * DAY_MS) / 1000;
    const nowSec = Math.floor(Date.now() / 1000);
    dispatch(
      mapActions.setTimeSliderSpan({
        mapId,
        timeSliderSpan: sliderSpanSec,
      }),
    );
    dispatch(
      mapActions.setTimeSliderCenterTime({
        mapId,
        timeSliderCenterTime: nowSec - sliderSpanSec / 2 + DAY_MS / 1000,
      }),
    );
    // Animation window: past 24 hours
    const now = new Date();
    const nowIso = now.toISOString();
    const dayAgoIso = new Date(now.valueOf() - DAY_MS).toISOString();
    dispatch(
      mapActions.setAnimationStartTime({
        mapId,
        animationStartTime: dayAgoIso,
      }),
    );
    dispatch(
      mapActions.setAnimationEndTime({ mapId, animationEndTime: nowIso }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return defaultLayerId;
};

const panelId = 'panelId';

const MapWithFlySafe: React.FC<{ mapId: string }> = ({ mapId }) => {
  const initialBbox = {
    srs: PROJECTION.EPSG_3857.value,
    // NL + Belgium + Germany (+ small padding)
    bbox: {
      left: 200375.08342789244,
      bottom: 5909489.863677087,
      right: 1870167.445326996,
      top: 7498924.477653491,
    },
  };
  const [radars, setRadars] = React.useState<FeatureCollection<Point> | null>(
    null,
  );
  React.useEffect(() => {
    getCachedStationLocations()
      .then((rad) => setRadars(rad))
      .catch((err) => {
        // Intentionally swallow: the map should still render without
        // the radar stations layer if this request fails.
        console.error('Failed to load station locations:', err);
      });
  }, []);

  const [selectedStationIds, setSelectedStationIds] = React.useState<string[]>(
    [],
  );
  const radarStyle = React.useMemo(
    () => createRadarStyleFunction(new Set(selectedStationIds)),
    [selectedStationIds],
  );

  const vidLayer = {
    service: `${window.location.origin.startsWith('http://localhost') ? 'https://flysafe.radar.acc.knmi.cloud' : window.location.origin}/wmts/1.0.0/WMTSCapabilities.xml`,
    enabled: true,
    id: 'flysafe_vid_layer',
    layerType: LayerType.wmtsMapLayer,
    name: 'birdtam',
    opacity: 0.9,
  };
  const soltLayer = {
    service: `${window.location.origin.startsWith('http://localhost') ? 'https://flysafe.radar.acc.knmi.cloud' : window.location.origin}/wmts/1.0.0/WMTSCapabilities.xml`,
    enabled: true,
    id: 'flysafe_solt_layer',
    layerType: LayerType.wmtsMapLayer,
    name: 'solarterminator',
    opacity: 0.25,
  };

  const defaultLayerId = useDefaultMapSettings({
    mapId,
    baseLayers: [
      {
        ...publicLayers.baseLayerArcGisCanvas,
        id: `baseArcGisCanvas-${mapId}`,
      },
    ],
    layers: [vidLayer, soltLayer],
    bbox: initialBbox.bbox,
    srs: initialBbox.srs,
    shouldAutoUpdate: true,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const dispatch = useDispatch();
  React.useEffect(() => {
    dispatch(syncGroupsActions.updateLinkedMap({ mapIds: [mapId], panelId }));
    return (): void => {
      dispatch(syncGroupsActions.updateLinkedMap({ mapIds: [], panelId }));
    };
  }, [dispatch, mapId]);

  // Style used to represent the country borders
  const blackOutlineStyle = new Style({
    stroke: new Stroke({
      color: '#000000',
      width: 1.5,
    }),
    fill: new Fill({
      color: 'rgba(0,0,0,0)',
    }),
  });

  return (
    <div
      style={{
        height: '100%',
        overflow: isMobile ? 'auto' : 'hidden',
        display: 'grid',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '2fr 2fr',
          minHeight: 0,
        }}
      >
        <div
          style={{
            position: 'relative',
            minHeight: isMobile ? '40vh' : 0,
          }}
        >
          <MapControls>
            <LayerManagerMapButtonConnect mapId={mapId} />
          </MapControls>
          <div
            style={{
              position: 'absolute',
              left: '0px',
              bottom: '0px',
              zIndex: 50,
              width: '100%',
            }}
          >
            <TimeSliderConnect mapId={mapId} sourceId="timeslider-1" />
          </div>
          <OlMapViewConnect displayTimeInMap mapId={mapId}>
            <HideMapPin />
            <OpenLayersFeatureLayer
              featureCollection={countries ?? undefined}
              style={blackOutlineStyle}
              zIndex={2000}
            />
            <OpenLayersFeatureLayer
              featureCollection={radars ?? undefined}
              style={radarStyle}
              zIndex={2000}
            />
          </OlMapViewConnect>
          <LayerManagerConnect />
          <LegendConnect mapId={mapId} multiLegend />
        </div>
        <div
          style={{
            position: 'relative',
            height: '100%',
            minHeight: 0,
            paddingLeft: isMobile ? 0 : '16px',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          <div style={{ height: '100%', minHeight: 0, overflowY: 'auto' }}>
            <FlySafeConnect
              defaultLayerId={defaultLayerId as string}
              onSelectedStationsChange={setSelectedStationIds}
              panelId={panelId}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const FlySafeDemo: React.FC = () => {
  const store = React.useMemo(
    () =>
      configureOpenGeoWebStore(undefined, layerSelectReducersMap, [
        ...(layerSelectMiddlewares ?? []),
        metronomeListener.middleware,
        syncGroupsListener.middleware,
      ] as Parameters<typeof configureOpenGeoWebStore>[2]),
    [],
  );

  sessionStorage.setItem(
    SessionStorageKey.CONFIG,
    JSON.stringify({
      GW_FLYSAFE_STATION_DATA_URL: `${window.location.origin.startsWith('http://localhost') ? 'https://flysafe.radar.acc.knmi.cloud' : window.location.origin}`,
      GW_FLYSAFE_SOLAR_TERMINATOR_URL: `${window.location.origin.startsWith('http://localhost') ? 'https://flysafe.radar.acc.knmi.cloud' : window.location.origin}`,
    }),
  );

  return (
    <ThemeProvider>
      <CoreStoreProvider store={store}>
        <MapWithFlySafe mapId="FlySafeMap" />
      </CoreStoreProvider>
    </ThemeProvider>
  );
};

export default FlySafeDemo;
